import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { FaPlus, FaEdit, FaTrash, FaMinusCircle } from "react-icons/fa";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { AuthContext } from "../context/AuthContext";
import RawMaterialModal from "../components/modals/AddEditRawMaterials";
import IssueRawMaterialsModal from "../components/modals/IssueRawMaterialsModal";
import { useNotificationModal } from "../hooks/useNotificationModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import "../styles/RawMaterials.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function RawMaterials() {
  const { userData } = useContext(AuthContext);
  const { showSuccess, showError } = useNotificationModal();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalMaterial, setModalMaterial] = useState(undefined);
  const [issueMaterial, setIssueMaterial] = useState(undefined);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState(null);

  const fetchRawMaterials = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${BASE_URL}/api/admin/raw-materials/get-all`,
        {
          params: { company_code: userData?.company_code },
        },
      );
      const processedMaterials = (data?.data ?? []).map((item) => {
        const processed = {
          material_id: item.material_id,
          name: item.material_name,
          size: item.material_size,
          qty: item.material_qty,
          qty_type: item.qty_type,
          description: item.description,
          last_issued_qty: item.last_issued_qty,
          last_issued_date: item.last_issued_date,
        };
        return processed;
      });
      setRawMaterials(processedMaterials);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load raw materials");
      setRawMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRawMaterials();
  }, [userData?.company_code]);

  const handleEdit = (material) => {
    setModalMaterial(material);
  };

  // Delete item
  const confirmDeleteMaterial = async () => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/api/admin/raw-materials/delete/${materialToDelete}`,
        {
          params: { company_code: userData?.company_code },
        },
      );

      if (response.data.success) {
        fetchRawMaterials();
      } else {
        setError(response.data.message || `Failed to delete material`);
      }
    } catch (err) {
      console.error(`Error deleting material:`, err);
      setError(err.response?.data?.message || `Error deleting material`);
    }
    setShowConfirmDelete(false);
    setMaterialToDelete(null);
  };

  const handleSuccess = (action) => {
    showSuccess(`Raw material ${action} successfully`, "Success");
    fetchRawMaterials();
    setModalMaterial(undefined);
  };

  const handleIssue = (material) => {
    setIssueMaterial(material);
  };

  const columns = [
    { accessorKey: "name", header: "Material Name" },
    { accessorKey: "size", header: "Size" },
    {
      accessorKey: "qty",
      header: "Available Quantity",
      cell: (info) => (
        <div style={{ textAlign: "right" }}>{info.getValue()}</div>
      ),
    },
    { accessorKey: "qty_type", header: "Quantity Type" },
    { accessorKey: "description", header: "Description" },
    {
      accessorKey: "last_issued_qty",
      header: "Last Issued Quantity",
      cell: (info) => (
        <div style={{ textAlign: "right" }}>{info.getValue()}</div>
      ),
    },
    {
      accessorKey: "last_issued_date",
      header: "Last Issued Date",
      cell: (info) => (
        <div style={{ textAlign: "right" }}>{info.getValue()}</div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      size: 120,
      cell: (info) => (
        <div className="action-buttons">
          <FaMinusCircle
            className="btn-icon issue"
            title="Issue"
            onClick={() => handleIssue(info.row.original)}
          />
          <FaEdit
            className="btn-icon edit"
            title="Edit"
            onClick={() => handleEdit(info.row.original)}
          />
          <FaTrash
            className="action-icon text-danger mt-1"
            onClick={() => {
              setMaterialToDelete(info.row.original.material_id);
              setShowConfirmDelete(true);
            }}
            title="Delete"
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: rawMaterials,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="style-management">
      <div className="style-header">
        <h1>Raw Material Management</h1>
        <button
          className="add-style-btn"
          onClick={() => {
            setModalMaterial(null);
          }}
        >
          <FaPlus size={14} /> Add New Raw Material
        </button>
      </div>

      <div className="table-container">
        {loading && (
          <div className="loading-message">
            <span>Loading raw materials...</span>
          </div>
        )}
        {error && (
          <div className="error-message">
            <span>{error}</span>
          </div>
        )}
        {!loading && !error && rawMaterials.length === 0 && (
          <div className="empty-message">
            <span>No raw materials found</span>
          </div>
        )}
        {!loading && !error && rawMaterials.length > 0 && (
          <table className="react-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      style={{ width: h.getSize ? `${h.getSize()}px` : "auto" }}
                    >
                      {h.isPlaceholder
                        ? null
                        : flexRender(h.column.columnDef.header, h.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RawMaterialModal
        show={modalMaterial !== undefined}
        material={modalMaterial}
        onSuccess={handleSuccess}
        onHide={() => {
          setModalMaterial(undefined);
        }}
      />

      <IssueRawMaterialsModal
        show={issueMaterial !== undefined}
        material={issueMaterial}
        onSuccess={handleSuccess}
        onHide={() => {
          setIssueMaterial(undefined);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showConfirmDelete}
        onHide={() => {
          setShowConfirmDelete(false);
          setMaterialToDelete(null);
        }}
        onConfirm={confirmDeleteMaterial}
        title={`Delete ${materialToDelete?.name || "Raw Material"}`}
        message={`Are you sure you want to delete ${materialToDelete?.name || "this raw material"}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={loading}
      />
    </div>
  );
}
